// @ts-check

import paypalStrategy from '../../methods/paypal/strategies/PaypalStrategy';
import paypalMethod from '../../methods/paypal/Paypal';

/**
 * @typedef {Object} PaypalConfig
 * @property {boolean} [sandbox] - Whether to use sandbox environment (defaults to true)
 * @property {string} [presentationMode] - Presentation mode ('auto', 'modal', 'popup', 'redirect')
 * @property {string} [locale] - Locale for PayPal SDK (e.g., 'en-US', 'es-MX')
 */

/**
 * @typedef {Object} PaypalFactoryConfig
 * @property {string} [engine] - The payment engine to use (defaults to 'paypal')
 * @property {PaypalConfig} paypalConfig - PayPal-specific configuration
 */

/**
 * Available strategies for PayPal payment method.
 * Maps engine names to their corresponding strategies.
 * Currently only supports the PayPal v6 SDK strategy.
 */
const engines = {
  paypal: paypalStrategy,
};

/**
 * Factory to create PayPal payment method instances.
 * Follows the standard factory pattern consistent with other payment methods.
 * @returns {object} An object with methods to create PayPal instances.
 */
function PaypalFactory() {
  /**
   * Creates a configured PayPal payment method instance.
   * @param {PaypalFactoryConfig} config - Configuration object
   * @returns {object} Configured PayPal method instance
   * @throws {Error} If configuration is invalid or engine not found
   */
  function create(config) {
    // Validate config parameter before destructuring
    if (!config) {
      throw new Error('PayPal factory config is required');
    }

    const { engine = 'paypal', paypalConfig } = config;

    // Validate engine availability
    const strategy = engines[engine];
    if (!strategy) {
      const availableEngines = Object.keys(engines).join(', ');
      throw new Error(
        `PayPal engine '${engine}' not found. Available engines: ${availableEngines}`,
      );
    }

    // Validate PayPal configuration
    if (!paypalConfig) {
      throw new Error('paypalConfig is required');
    }

    // Validate configuration using the strategy
    try {
      strategy.validateConfig(paypalConfig);
    } catch (error) {
      throw new Error(`Invalid PayPal configuration: ${error.message}`);
    }

    // Configure the method with the selected strategy
    paypalMethod.setStrategy(strategy);

    // Return the configured method
    return paypalMethod;
  }

  return { create };
}

// Create singleton instance
const paypalFactory = PaypalFactory();

export default paypalFactory;
